import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/lib/usePageMeta";
import { env } from "@/lib/env";
import { buildCseUrl } from "@/lib/cse";
import { useLocale } from "@/i18n/useLocale";
import { searchFor } from "@/content/platforms";

const ROBOTS_META_ID = "search-robots-meta";

export default function SearchPage(): JSX.Element {
  const t = searchFor(useLocale());
  const [params] = useSearchParams();
  const query = params.get("q") ?? "";
  const trimmed = query.trim();

  usePageMeta({
    title: t.meta.title,
    description: t.meta.description,
  });

  useEffect(() => {
    let robots = document.head.querySelector<HTMLMetaElement>(
      `meta[id="${ROBOTS_META_ID}"]`,
    );
    if (!robots) {
      robots = document.createElement("meta");
      robots.id = ROBOTS_META_ID;
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = "noindex, follow";

    let timeoutId: number | undefined;
    if (trimmed) {
      timeoutId = window.setTimeout(() => {
        window.location.replace(buildCseUrl(env.cseCxId, trimmed));
      }, 50);
    }

    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      robots?.remove();
    };
  }, [trimmed]);

  if (!trimmed) {
    return (
      <main className="container-page py-24 md:py-32 text-center">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">{t.heading}</h1>
        <p className="text-fg-muted max-w-xl mx-auto mb-8">
          This page is the destination of the brand-search box that appears on
          Google search results for Flex Fund. To search from here directly,
          add <code>?q=your+query</code> to the URL.
        </p>
        <a href="/" className="text-brand-emphasis hover:underline">
          ← Back to home
        </a>
      </main>
    );
  }

  return (
    <main className="container-page py-24 md:py-32 text-center">
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        Searching for &ldquo;{trimmed}&rdquo;…
      </h1>
      <p className="text-fg-muted">{t.redirecting}</p>
    </main>
  );
}
